import React, { useEffect, useRef } from "react";
import { Animated, Easing, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import { colors, motion, radius } from "../theme/tokens";

export type LoadableState = "initial-loading" | "ready" | "refreshing" | "empty" | "error";

export function SkeletonBlock({
  style,
  reduceMotion = false,
}: {
  style?: StyleProp<ViewStyle>;
  reduceMotion?: boolean;
}) {
  const opacity = useRef(new Animated.Value(0.5)).current;
  useEffect(() => {
    if (reduceMotion) {
      opacity.setValue(0.65);
      return undefined;
    }
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0.82,
          duration: motion.sectionReveal * 3,
          easing: Easing.inOut(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.5,
          duration: motion.sectionReveal * 3,
          easing: Easing.inOut(Easing.cubic),
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [opacity, reduceMotion]);
  return <Animated.View pointerEvents="none" style={[styles.block, style, { opacity }]} />;
}

export function ShelfSkeleton({
  cardWidth,
  landscape = false,
  count = 6,
  horizontalPadding = 56,
  reduceMotion = false,
}: {
  cardWidth: number;
  landscape?: boolean;
  count?: number;
  horizontalPadding?: number;
  reduceMotion?: boolean;
}) {
  const cardHeight = Math.round(cardWidth * (landscape ? 0.5625 : 1.5));
  return (
    <View style={[styles.shelf, { paddingHorizontal: horizontalPadding }]}>
      {Array.from({ length: count }, (_, index) => (
        <SkeletonBlock
          key={`skeleton-${index}`}
          reduceMotion={reduceMotion}
          style={{ width: cardWidth, height: cardHeight }}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  block: {
    borderRadius: radius.medium,
    backgroundColor: colors.surfaceRaised,
  },
  shelf: {
    flexDirection: "row",
    gap: 8,
    overflow: "hidden",
  },
});
